// UserInfoForm.js
import React, { useState, useCallback, useEffect } from "react";
import {
  TextField,
  Button,
  Box,
  Typography,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import Particles from "react-tsparticles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import {
  validateEmail,
  validateOcbcEmail,
  debounce,
} from "../utils/validation";
import "./UserInfoForm.css";
import { loadFull } from "tsparticles";

const UserInfoForm = ({
  userInfo,
  setUserInfo,
  handleSendOtp,
  handleVerifyOtp,
  otpSent,
  otpVerified,
  otp,
  setOtp,
  errorMessage,
}) => {
  const [showConfetti, setShowConfetti] = useState(false);
  const [otpTimer, setOtpTimer] = useState(300); // 5 minutes in seconds
  const [emailValidation, setEmailValidation] = useState({
    isValid: true,
    error: null,
  });
  const [ocbcEmailValidation, setOcbcEmailValidation] = useState({
    isValid: true,
    error: null,
  });
  const [isDirty, setIsDirty] = useState(false);
  const [isOcbcDirty, setIsOcbcDirty] = useState(false);

  const particlesInit = useCallback(async (engine) => {
    await loadFull(engine);
  }, []);

  useEffect(() => {
    let interval;
    if (otpSent && !otpVerified && otpTimer > 0) {
      interval = setInterval(() => {
        setOtpTimer((prevTimer) => prevTimer - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [otpSent, otpVerified, otpTimer]);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  // Debounced email validation
  const debouncedValidateEmail = useCallback(
    debounce((email) => {
      const result = validateEmail(email);
      setEmailValidation(result);
      if (result.isValid && result.normalizedEmail !== email) {
        setUserInfo((prev) => ({ ...prev, email: result.normalizedEmail }));
      }
    }, 300),
    []
  );

  const handleEmailChange = (e) => {
    const email = e.target.value;
    setUserInfo((prev) => ({ ...prev, email }));
    setIsDirty(true);
    debouncedValidateEmail(email);
  };

  // Debounced OCBC email validation
  const debouncedValidateOcbcEmail = useCallback(
    debounce((email) => {
      const result = validateOcbcEmail(email);
      setOcbcEmailValidation(result);
      if (result.isValid && result.normalizedEmail !== email) {
        setUserInfo((prev) => ({ ...prev, ocbcEmail: result.normalizedEmail }));
      }
    }, 300),
    []
  );

  const handleOcbcEmailChange = (e) => {
    const email = e.target.value;
    setUserInfo((prev) => ({ ...prev, ocbcEmail: email }));
    setIsOcbcDirty(true);
    debouncedValidateOcbcEmail(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!otpSent) {
      const validation = validateEmail(userInfo.email);
      if (!validation.isValid) {
        setEmailValidation(validation);
        return;
      }
      await handleSendOtp();
    } else if (!otpVerified) {
      const success = await handleVerifyOtp();
      if (success) {
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 5000);
      }
    }
  };

  return (
    <Box className="user-info-form-container">
      <form className="user-info-form" onSubmit={handleSubmit}>
        <Typography variant="h5">Just a moment...</Typography>
        <Typography variant="subtitle1">
          We'll save your results so you can easily access them again.
        </Typography>
        <TextField
          label="Name"
          variant="outlined"
          value={userInfo.name}
          onChange={(e) => setUserInfo({ ...userInfo, name: e.target.value })}
          required
          fullWidth
          margin="normal"
          disabled={otpSent}
        />
        <TextField
          label="Email"
          variant="outlined"
          value={userInfo.email}
          onChange={handleEmailChange}
          onBlur={() => {
            const validation = validateEmail(userInfo.email);
            setEmailValidation(validation);
            if (validation.isValid) {
              setUserInfo((prev) => ({
                ...prev,
                email: validation.normalizedEmail,
              }));
            }
          }}
          error={isDirty && !emailValidation.isValid}
          helperText={isDirty && emailValidation.error}
          required
          fullWidth
          margin="normal"
          disabled={otpSent}
          InputProps={{
            endAdornment: isDirty && (
              <InputAdornment position="end">
                {emailValidation.isValid ? (
                  <CheckCircleIcon color="success" />
                ) : (
                  <ErrorIcon color="error" />
                )}
              </InputAdornment>
            ),
          }}
        />
        <TextField
          label="OCBC Email"
          variant="outlined"
          value={userInfo.ocbcEmail}
          onChange={handleOcbcEmailChange}
          onBlur={() => {
            const validation = validateOcbcEmail(userInfo.ocbcEmail);
            setOcbcEmailValidation(validation);
            if (validation.isValid) {
              setUserInfo((prev) => ({
                ...prev,
                ocbcEmail: validation.normalizedEmail,
              }));
            }
          }}
          error={isOcbcDirty && !ocbcEmailValidation.isValid}
          helperText={isOcbcDirty && ocbcEmailValidation.error}
          required
          fullWidth
          margin="normal"
          disabled={otpSent}
          InputProps={{
            endAdornment: isOcbcDirty && (
              <InputAdornment position="end">
                {ocbcEmailValidation.isValid ? (
                  <CheckCircleIcon color="success" />
                ) : (
                  <ErrorIcon color="error" />
                )}
              </InputAdornment>
            ),
          }}
        />
        <TextField
          label="Career Concern"
          variant="outlined"
          value={userInfo.careerConcern}
          onChange={(e) =>
            setUserInfo({ ...userInfo, careerConcern: e.target.value })
          }
          required
          fullWidth
          margin="normal"
          disabled={otpSent}
        />
        {/* <TextField
          label="Location"
          variant="outlined"
          value={userInfo.location || ""}
          onChange={(e) =>
            setUserInfo({ ...userInfo, location: e.target.value })
          }
          required
          fullWidth
          margin="normal"
          disabled={otpSent}
          hidden={true}
          visibility="hidden"
        /> */}
        <TextField
          label="OCBC Tenure in years"
          variant="outlined"
          value={userInfo.ocbcTenure}
          onChange={(e) =>
            setUserInfo({ ...userInfo, ocbcTenure: e.target.value })
          }
          required
          fullWidth
          margin="normal"
          disabled={otpSent}
          type="number"
          inputProps={{
            min: 0,
            step: 1,
          }}
        />
        <TextField
          label="Current Role Tenure in years"
          variant="outlined"
          value={userInfo.currentRoleTenure}
          onChange={(e) =>
            setUserInfo({ ...userInfo, currentRoleTenure: e.target.value })
          }
          required
          fullWidth
          margin="normal"
          disabled={otpSent}
          type="number"
          inputProps={{
            min: 0,
            step: 1,
          }}
        />
        <FormControl
          fullWidth
          margin="normal"
          variant="outlined"
          required
          disabled={otpSent}
        >
          <InputLabel id="age-group-label">Age Group</InputLabel>
          <Select
            labelId="age-group-label"
            id="age-group"
            value={userInfo.ageGroup || ""}
            label="Age Group"
            onChange={(e) =>
              setUserInfo({ ...userInfo, ageGroup: e.target.value })
            }
          >
            <MenuItem value="18-24">18-24</MenuItem>
            <MenuItem value="25-34">25-34</MenuItem>
            <MenuItem value="35-44">35-44</MenuItem>
            <MenuItem value="45-54">45-54</MenuItem>
            <MenuItem value="55-64">55-64</MenuItem>
            <MenuItem value="65 and Above">65 and Above</MenuItem>
          </Select>
        </FormControl>

        {otpSent && !otpVerified && (
          <>
            <TextField
              label="OTP"
              variant="outlined"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              required
              fullWidth
              margin="normal"
              inputProps={{
                maxLength: 6,
                pattern: "[0-9]*",
              }}
              helperText={`OTP valid for: ${formatTime(otpTimer)}`}
            />
          </>
        )}
        {errorMessage && <Typography color="error">{errorMessage}</Typography>}
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={
            (otpSent && otpTimer === 0) ||
            (!otpSent &&
              (!emailValidation.isValid ||
                !ocbcEmailValidation.isValid ||
                !userInfo.email ||
                !userInfo.ocbcEmail ||
                !userInfo.name))
          }
        >
          {otpSent ? "Verify Email OTP" : "Verify & View Results"}
        </Button>
      </form>
    </Box>
  );
};

export default UserInfoForm;
